import { refreshTokens } from '@common/auth';
import axios, { AxiosError, AxiosResponse } from 'axios';
import { getTokens, clearTokens } from '@store';

const fallbackErrorMessage = 'Oops! Something didn’t go as planned.';

declare module 'axios' {
  /**
   * Requests that handle a 404 themselves (e.g. treating it as a soft
   * "not found" and returning null) can set this flag on their config to
   * opt out of the global redirect to the Not Found page.
   */
  export interface AxiosRequestConfig {
    skipNotFoundRedirect?: boolean;
  }
}

export const axiosExternalInstance = axios.create({
  baseURL: import.meta.env.VITE_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

export const axiosInstance = axios.create({
  baseURL: import.meta.env.VITE_BASE_URL,
});

axiosInstance.interceptors.request.use(config => {
  const token = localStorage.getItem('token');

  if (token) config.headers['Authorization'] = `Bearer ${token}`;

  return config;
});

let refreshQueue: (() => void)[] = [];

function flushRefreshQueue() {
  for (const request of refreshQueue) {
    try {
      request();
    } catch (err) {
      // eslint-disable-next-line no-console
      console.log('Refreshed token queue error', err);
    }
  }
  refreshQueue = [];
}

export async function safeRefreshTokens() {
  const startTokens = getTokens();
  await navigator.locks.request('auth-refresh', async () => {
    let currentTokens = getTokens();
    let tokenChanged =
      currentTokens.token !== startTokens.token ||
      currentTokens.refreshToken !== startTokens.refreshToken;

    if (tokenChanged) {
      flushRefreshQueue();
      return;
    }

    try {
      await refreshTokens(currentTokens.refreshToken);
      flushRefreshQueue();
    } catch (error) {
      const status = (error as { response?: { status?: number } })?.response
        ?.status;

      currentTokens = getTokens();
      tokenChanged =
        currentTokens.token !== startTokens.token ||
        currentTokens.refreshToken !== startTokens.refreshToken;

      if (tokenChanged) {
        flushRefreshQueue();
        return;
      }

      if (status !== undefined && status >= 400 && status < 500) {
        clearTokens();
        refreshQueue = [];
        window.location.href = '/auth/login';
      }
      throw error;
    }
  });
}

axiosInstance.interceptors.response.use(
  response => response,
  (error: AxiosError) => {
    // if (error.response?.status === 403) {
    //   window.location.href = '/access-denied';
    //   return Promise.reject(error);
    // }

    if (error.response?.status === 404 && !error.config?.skipNotFoundRedirect) {
      window.location.href = '/404';
      return Promise.reject(error);
    }

    if (
      error.response?.status !== 401 ||
      window.location.pathname.startsWith('/auth')
    ) {
      return Promise.reject(error);
    }

    const originalRequest = error.config;
    if (!originalRequest) {
      return Promise.reject(error);
    }

    if (
      (originalRequest as typeof originalRequest & { _retry?: boolean })._retry
    ) {
      return Promise.reject(error);
    }

    (originalRequest as typeof originalRequest & { _retry?: boolean })._retry =
      true;

    return new Promise((resolve, reject) => {
      refreshQueue.push(() => {
        originalRequest.headers['Authorization'] =
          `Bearer ${getTokens().token}`;
        axiosInstance(originalRequest).then(resolve).catch(reject);
      });

      safeRefreshTokens().catch(err => {
        reject(err);
      });
    });
  },
);

/**
 * Handles errors on file downloads
 */
axiosInstance.interceptors.response.use(
  response => response,
  error => {
    const { response } = error;
    if (
      response &&
      response.data instanceof Blob &&
      response.headers['content-type'] &&
      response.headers['content-type'].includes('application/json')
    ) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          if (reader.result) {
            try {
              let resultText: string;
              if (typeof reader.result === 'string') {
                resultText = reader.result;
              } else {
                resultText = new TextDecoder('utf-8').decode(reader.result);
              }
              response.data = JSON.parse(resultText);
            } catch {
              response.data = { message: fallbackErrorMessage };
            }
          }
          reject(error);
        };
        reader.onerror = () => {
          response.data = { message: fallbackErrorMessage };
          reject(error);
        };
        reader.readAsText(response.data);
      });
    }
    return Promise.reject(error);
  },
);

/**
 * Accepts generic types for request body and response body
 */
export async function postRequest<T, K>(
  url: string,
  payload: T,
): Promise<AxiosResponse<K>> {
  return await axiosInstance.post<K>(url, payload);
}
